import { Box, Button, Flex, IconFont, Modal, Popover, Text, Toast, Tooltip } from '@byecode/ui'
import { APPLICATION_VERSION_STATUS } from '@lighthouse/core'
import type { EnvDiffs } from '@lighthouse/shared'
import {
    APPLICATION_ENV_PROD,
    timeDistance,
    useApplicationContext,
    useAtomAction,
    useAtomAsyncAction,
    useAtomData
} from '@lighthouse/shared'
import { hasCustomAttribute } from '@lighthouse/tools'
import { useSetAtom } from 'jotai'
import { find } from 'rambda'
import { useCallback, useEffect, useRef, useState } from 'react'
import useSWR from 'swr'

import { mergeAppVersionAtom, setApplicationListAtom, updateAppStatusAtom } from '@/atoms/application/action'
import { appAtom, AppPublishStateAtom } from '@/atoms/application/state'
import { UrlPreview } from '@/components/ApplicationPublish/UrlPreview'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import * as srv from '@/services'

import type { PublishedPanelType } from '../../ApplicationHeader/PublishedContent'
import { PublishedContent, publishEmitter } from '../../ApplicationHeader/PublishedContent'
import { MergeConfirm } from './MergeConfirm'

interface ShareButtonProps {
    popoverOpened?: boolean
    onPopoverChange?: (v: boolean) => void
}

export const ShareButton = ({ popoverOpened, onPopoverChange }: ShareButtonProps) => {
    const [open, setOpen] = useState(false)
    const [defaultPanel, setDefaultPanel] = useState<PublishedPanelType | undefined>()
    const rootRef = useRef<HTMLDivElement>(null)
    const updateAppStatusAction = useAtomAsyncAction(updateAppStatusAtom)
    const { run: setApplicationList } = useAtomAction(setApplicationListAtom)
    const appEnvId = useCurrentEnvId()
    const appId = useCurrentAppId()
    const [diffData, setDiffData] = useState<EnvDiffs | undefined>(undefined)
    const mergeAppAction = useAtomAsyncAction(mergeAppVersionAtom)
    const setAppPublishState = useSetAtom(AppPublishStateAtom)
    const setAppInfo = useSetAtom(appAtom)
    const { hasUpdate: hasUpdateContent, mutateHasUpdate } = useApplicationContext()

    const { data: env } = useSWR(
        ['getEnv'],
        () => {
            return srv.getEnv(appEnvId)
        },
        {
            revalidateOnFocus: false
        }
    )

    const handleOffline = useCallback(() => {
        updateAppStatusAction.run(APPLICATION_VERSION_STATUS.OFFLINE)
        setApplicationList(draft => {
            const app = find(item => item.id === appId, draft)
            if (app) {
                app.isPublish = false
            }
        })
        Toast.success('取消发布成功')
    }, [appId, setApplicationList, updateAppStatusAction])

    const handleMerge = useCallback(async () => {
        const updateTime = await mergeAppAction.run()
        if (!updateTime) {
            return
        }

        setAppInfo(draft => {
            draft.updatedTime = updateTime
        })

        Toast.success('更新发布成功')
        // 手动改一下状态
        mutateHasUpdate?.(false)
        // 打开烟花弹窗
        setAppPublishState(draft => {
            draft.isSuccess = true
        })
    }, [mergeAppAction, mutateHasUpdate, setAppInfo, setAppPublishState])

    const getIsDiffs = useCallback(async () => {
        const envDiffs = await srv.getEnvDiffs(appEnvId, APPLICATION_ENV_PROD)
        setDiffData(envDiffs)
        const isAdditions = envDiffs?.additions && envDiffs.additions.length > 0
        const isDeletions = envDiffs?.deletions && envDiffs.deletions.length > 0
        const isErrors = envDiffs?.errors && envDiffs.errors.length > 0
        const isUpdates = envDiffs?.updates && envDiffs.updates.length > 0

        return isAdditions || isDeletions || isErrors || isUpdates
    }, [appEnvId])

    const [diffLoading, setDiffLoading] = useState(false)

    publishEmitter.on('openPublishedPanel', params => {
        onPopoverChange?.(true)
        setDefaultPanel(params)
    })

    useEffect(() => {
        if (!popoverOpened) {
            setDefaultPanel(undefined)
        }
    }, [popoverOpened])

    const handleCheck = useCallback(async () => {
        if (!hasUpdateContent) {
            Toast.info('暂无需要更新的内容', { icon: 'Smile', iconColor: 'var(--color-main)' })
            return
        }
        // const isCanPublic = handleCanPrivateTemplate()
        // if (!isCanPublic) {
        //     return
        // }
        setDiffLoading(true)
        const isDiff = await getIsDiffs()
        setDiffLoading(false)

        if (isDiff) {
            setOpen(true)
        } else {
            handleMerge()
        }
    }, [getIsDiffs, handleMerge, hasUpdateContent])

    const updateTime = useAtomData(
        appAtom,
        useCallback(s => s.updatedTime, [])
    )

    const confirmOpenedRef = useRef(false)

    return (
        <>
            <Popover
                opened={popoverOpened}
                onChange={v => !confirmOpenedRef.current && onPopoverChange?.(v)}
                width={390}
                position="bottom-end"
                withinPortal
                outsidePress={e => {
                    // 打开分享弹窗后，点击modal不能关闭popover
                    if (e.target instanceof HTMLElement) {
                        return !hasCustomAttribute(e.target, 'data-modal-ignore-click-away')
                    }
                    return true
                }}
            >
                <Tooltip title="分享">
                    <Popover.Target>
                        <Button type="primary" icon={<IconFont type="SquareShareLine" size={16} />}>
                            <Text>分享</Text>
                            {hasUpdateContent && (
                                <Box
                                    style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        backgroundColor: 'var(--color-yellow-500)'
                                    }}
                                />
                            )}
                        </Button>
                    </Popover.Target>
                </Tooltip>
                <Popover.Dropdown
                    ref={rootRef}
                    styles={{
                        dropdown: {
                            borderRadius: 12,
                            padding: '24px !important'
                        }
                    }}
                >
                    <PublishedContent
                        defaultPanel={defaultPanel}
                        showShareContent
                        header={
                            <>
                                <Flex alignItems="center" justifyContent="space-between" mb={16}>
                                    <Flex direction="column" gap={8}>
                                        <Text weight={500} lineHeight="24px">
                                            {hasUpdateContent ? '将内容更新到线上' : '已发布所有内容'}
                                        </Text>
                                        <Text size={14} color="var(--color-gray-500)">
                                            上次发布：{updateTime && timeDistance(updateTime)}
                                        </Text>
                                    </Flex>
                                    <IconFont
                                        type="CloudCheck"
                                        size={40}
                                        color={hasUpdateContent ? 'var(--color-yellow-500)' : 'var(--color-green-500)'}
                                    />
                                </Flex>

                                <UrlPreview isProdHost onLink={() => onPopoverChange?.(false)} />
                            </>
                        }
                        footer={
                            <Flex gap={8} mt={16}>
                                <Button
                                    block
                                    size="lg"
                                    loading={updateAppStatusAction.loading}
                                    disabled={mergeAppAction.loading}
                                    onClick={async () => {
                                        confirmOpenedRef.current = true
                                        const isConfirm = await Modal.confirm({
                                            title: '您确定要取消发布应用吗？',
                                            content: '取消发布后，用户将无法访问应用，重新发布后可恢复。',
                                            okStatus: 'primary',
                                            okText: '确定'
                                        })

                                        confirmOpenedRef.current = false
                                        if (isConfirm) {
                                            handleOffline()
                                        }
                                    }}
                                >
                                    {updateAppStatusAction.loading ? '取消发布中' : '取消发布'}
                                </Button>
                                <Button
                                    block
                                    type="primary"
                                    icon={<IconFont type="ArrowsClockwise" size={16} color="var(--color-white)" />}
                                    size="lg"
                                    loading={diffLoading || mergeAppAction.loading}
                                    disabled={!hasUpdateContent || updateAppStatusAction.loading}
                                    onClick={handleCheck}
                                >
                                    {hasUpdateContent ? '更新发布' : '无更新内容'}
                                    {hasUpdateContent && (
                                        <Box
                                            style={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                backgroundColor: 'var(--color-yellow-500)'
                                            }}
                                        />
                                    )}
                                </Button>
                            </Flex>
                        }
                        onClose={() => onPopoverChange?.(false)}
                    />
                </Popover.Dropdown>
            </Popover>
            {open && <MergeConfirm data={diffData} onOpen={setOpen} />}
        </>
    )
}
